import React, { useState, useEffect } from 'react';
import { Loader2, Square, Briefcase } from 'lucide-react';
import { formatCSTTime, calculateDuration } from '../../utils/time';
import { TaskConfirmationDialog } from './TaskConfirmationDialog';

interface ActiveTask {
  task_id: number;
  description: string;
  start_time: number;
  wo_number?: string;
  report_id?: number;
}

interface ActiveTasksModalProps {
  tasks: ActiveTask[];
  onClose: () => void;
  onTaskComplete: (taskId: number, startTime: number) => Promise<void>;
  loading: boolean;
}

export function ActiveTasksModal({ 
  tasks, 
  onClose, 
  onTaskComplete,
  loading,
}: ActiveTasksModalProps) {
  const [completingTaskId, setCompletingTaskId] = useState<number | null>(null);
  const [confirmTask, setConfirmTask] = useState<ActiveTask | null>(null);

  useEffect(() => {
    // Close modal when all tasks are completed
    if (tasks.length === 0) {
      onClose();
    }
  }, [tasks.length, onClose]);

  const handleTaskComplete = async (task: ActiveTask) => {
    setCompletingTaskId(task.task_id);
    await onTaskComplete(task.task_id, task.start_time);
    setCompletingTaskId(null);
    setConfirmTask(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-light-card dark:bg-dark-card rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Active Tasks
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          {tasks.map((task) => (
            <div
              key={task.task_id}
              className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4 space-y-3"
            >
              <div className="flex justify-between items-start gap-4">
                <div>
                  {task.wo_number && (
                    <div className="flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 mb-1">
                      <Briefcase className="h-4 w-4" />
                      WO #{task.wo_number}
                    </div>
                  )}
                  <p className="text-gray-900 dark:text-gray-100">{task.description}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Started: {formatCSTTime(new Date(task.start_time))}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Duration: {calculateDuration(task.start_time, Date.now())}
                  </p>
                </div>
                <button
                  onClick={() => setConfirmTask(task)}
                  disabled={loading || completingTaskId === task.task_id}
                  className="btn-danger"
                >
                  {completingTaskId === task.task_id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                  Stop Task
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {confirmTask && (
        <TaskConfirmationDialog
          taskDescription={confirmTask.description}
          onConfirm={() => handleTaskComplete(confirmTask)}
          onCancel={() => setConfirmTask(null)}
          loading={loading && completingTaskId === confirmTask.task_id}
        />
      )}
    </div>
  );
}